import { nextTick, onActivated, onMounted, ref, unref, watch } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { buildRequestErrorMessage, formatFileSize } from './shared';

const DEFAULT_REDIS_PREFIX = 'api:olinfo';

function buildDefaultRedisPath(testVersion) {
  const version = String(testVersion || '').trim();
  return version ? `${DEFAULT_REDIS_PREFIX}:${version}` : DEFAULT_REDIS_PREFIX;
}

export function useMazdaJsonRedis(mazdaPost, serverName) {
  const currentTestVersion = ref('');
  const targetRedisPath = ref(DEFAULT_REDIS_PREFIX);
  const newRedisPath = ref(DEFAULT_REDIS_PREFIX);
  const RedisPathDialog = ref(false);
  const uploadRef = ref(null);
  const currentFile = ref(null);
  const currentFiles = ref([]);
  const jsonUploadResult = ref(null);
  const jsonPreviewKeys = ref([]);
  const jsonErrors = ref([]);
  const loading = ref(false);
  const pathLoading = ref(false);

  const applyDefaultRedisPath = (testVersion) => {
    currentTestVersion.value = String(testVersion || '').trim();
    const path = buildDefaultRedisPath(currentTestVersion.value);
    targetRedisPath.value = path;
    newRedisPath.value = path;
  };

  const loadDefaultRedisPath = async (silent = true) => {
    pathLoading.value = true;
    try {
      const response = await mazdaPost('soVersionInfo');
      const data = response?.data || {};
      if (!data.success) {
        if (!silent) {
          ElMessage.error(data.message || '读取测试版本失败');
        }
        return;
      }

      applyDefaultRedisPath(data.testVersion);
      if (!silent) {
        ElMessage.success(`已刷新默认路径：${targetRedisPath.value}`);
      }
    } catch (error) {
      if (!silent) {
        ElMessage.error(buildRequestErrorMessage('读取测试版本失败', error));
      }
    } finally {
      pathLoading.value = false;
    }
  };

  const beforeUpload = (file) => {
    const isJson = file.name.toLowerCase().endsWith('.json');
    if (!isJson) {
      ElMessage.error('只能上传 JSON 文件');
      return false;
    }
    return true;
  };

  const handleFileChange = (file, fileList) => {
    currentFile.value = file.raw || null;
    currentFiles.value = fileList
      .map((item) => item.raw)
      .filter(Boolean)
      .filter((f) => String(f.name || '').toLowerCase().endsWith('.json'));
  };

  const cancelFile = () => {
    if (uploadRef.value) {
      uploadRef.value.clearFiles();
    }
    currentFile.value = null;
    currentFiles.value = [];
    jsonUploadResult.value = null;
    jsonPreviewKeys.value = [];
    jsonErrors.value = [];
  };

  const handleExceed = (files) => {
    ElMessage.warning(`最多只能上传10个文件，您选择了${files.length}个文件，请删除部分文件后重新上传！`);
  };

  const showRedisPath = () => {
    newRedisPath.value = targetRedisPath.value;
    RedisPathDialog.value = true;
  };

  const canceUpdataRedisPath = () => {
    RedisPathDialog.value = false;
    newRedisPath.value = targetRedisPath.value;
  };

  const confirmUpdataRedisPath = () => {
    const path = String(newRedisPath.value || '').trim().replace(/:+$/, '');
    if (!path) {
      ElMessage.warning('Redis 路径不能为空');
      return;
    }
    targetRedisPath.value = path;
    newRedisPath.value = path;
    RedisPathDialog.value = false;
  };

  const confirmOnLine = async () => {
    if (!currentFiles.value.length) {
      ElMessage.warning('请先选择有效的 JSON 文件');
      return;
    }
    if (!targetRedisPath.value) {
      ElMessage.warning('请先设置 Redis 路径');
      return;
    }

    const hasInvalid = currentFiles.value.some((file) => !String(file.name || '').toLowerCase().endsWith('.json'));
    if (hasInvalid) {
      ElMessage.error('只能上传 .json 文件');
      return;
    }

    loading.value = true;
    jsonUploadResult.value = null;
    jsonPreviewKeys.value = [];
    jsonErrors.value = [];

    try {
      await ElMessageBox.confirm(
        `确定将 ${currentFiles.value.length} 个 JSON 文件写入 Redis 路径：\n${targetRedisPath.value}\n\n当前测试版本：${currentTestVersion.value || '-'}\n目标库：DB1 (mazda)`,
        '确认上传路径',
        {
          confirmButtonText: '确定上传',
          cancelButtonText: '取消',
          type: 'warning',
        },
      );

      const formData = new FormData();
      currentFiles.value.forEach((file) => {
        formData.append('files', file);
      });
      formData.append('newRedisPath', targetRedisPath.value);

      const response = await mazdaPost('uploadJson', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });

      const data = response.data || {};
      jsonUploadResult.value = data;
      jsonPreviewKeys.value = Array.isArray(data.previewKeys) ? data.previewKeys : [];
      jsonErrors.value = Array.isArray(data.errors) ? data.errors : [];

      if (data.success) {
        ElMessage.success(data.message || '上传成功');
        await nextTick();
        if (uploadRef.value) {
          uploadRef.value.clearFiles();
        }
        currentFile.value = null;
        currentFiles.value = [];
      } else {
        ElMessage.error(data.message || '上传失败');
      }
    } catch (error) {
      if (error === 'cancel' || error === 'close' || error?.message === 'cancel') {
        return;
      }
      if (error?.response) {
        jsonUploadResult.value = error.response.data || null;
        jsonPreviewKeys.value = Array.isArray(error.response.data?.previewKeys) ? error.response.data.previewKeys : [];
        jsonErrors.value = Array.isArray(error.response.data?.errors) ? error.response.data.errors : [];
      }
      ElMessage.error(buildRequestErrorMessage('上传失败', error));
    } finally {
      loading.value = false;
    }
  };

  onMounted(() => {
    loadDefaultRedisPath(true);
  });

  onActivated(() => {
    loadDefaultRedisPath(true);
  });

  if (serverName) {
    watch(
      () => unref(serverName),
      () => loadDefaultRedisPath(true),
    );
  }

  return {
    currentTestVersion,
    targetRedisPath,
    newRedisPath,
    RedisPathDialog,
    uploadRef,
    currentFile,
    currentFiles,
    jsonUploadResult,
    jsonPreviewKeys,
    jsonErrors,
    loading,
    pathLoading,
    formatFileSize,
    beforeUpload,
    handleFileChange,
    cancelFile,
    handleExceed,
    confirmOnLine,
    showRedisPath,
    canceUpdataRedisPath,
    confirmUpdataRedisPath,
    loadDefaultRedisPath,
  };
}
